import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Image as ImageIcon,
  LogOut,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  elektrykAdminCreateRealizacja,
  elektrykAdminDeleteImage,
  elektrykAdminDeleteRealizacja,
  elektrykAdminListRealizacje,
  elektrykAdminLock,
  elektrykAdminStatus,
  elektrykAdminUnlock,
  elektrykAdminUpdateRealizacja,
  elektrykAdminUploadImage,
} from "@/lib/elektryk-admin.functions";
import { ELEKTRYK_ADMIN_TOKEN_KEY } from "@/lib/elektryk-admin-attacher";
import { VOLT } from "@/lib/elektryk-gallery";

type AdminRealizacja = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  location: string | null;
  date_label: string | null;
  published: boolean;
  sort_order: number;
  created_at: string;
  images: { id: string; url: string; alt: string | null; sort_order: number }[];
};

export const Route = createFileRoute("/demo/elektryk_/admin")({
  head: () => ({
    meta: [
      { title: "Panel administratora — ELEKTROPROFI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const status = useServerFn(elektrykAdminStatus);
  const [unlocked, setUnlocked] = useState<boolean | null>(null);

  useEffect(() => {
    const hasToken =
      typeof window !== "undefined" && !!window.localStorage.getItem(ELEKTRYK_ADMIN_TOKEN_KEY);
    if (!hasToken) {
      setUnlocked(false);
      return;
    }
    status()
      .then((r) => {
        if (!r.unlocked) {
          try {
            window.localStorage.removeItem(ELEKTRYK_ADMIN_TOKEN_KEY);
          } catch {}
        }
        setUnlocked(r.unlocked);
      })
      .catch(() => setUnlocked(false));
  }, [status]);

  return (
    <div className="min-h-screen bg-[#070708] font-sans text-[#F5F5F7] antialiased selection:bg-[#00F0FF] selection:text-black overflow-x-clip">
      <TopBar unlocked={!!unlocked} onLocked={() => setUnlocked(false)} />
      <main className="mx-auto max-w-6xl px-5 py-10 md:px-10 md:py-14">
        {unlocked === null ? (
          <div className="grid place-items-center py-32 text-sm text-white/50">Ładowanie…</div>
        ) : !unlocked ? (
          <UnlockForm onUnlocked={() => setUnlocked(true)} />
        ) : (
          <Dashboard />
        )}
      </main>
    </div>
  );
}

function TopBar({ unlocked, onLocked }: { unlocked: boolean; onLocked: () => void }) {
  const lock = useServerFn(elektrykAdminLock);
  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#070708]/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 md:px-10">
        <Link
          to="/demo/elektryk"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70 transition hover:border-[#00F0FF]/40 hover:text-white"
        >
          <ArrowLeft size={12} /> ELEKTROPROFI
        </Link>
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">Panel admina</div>
        {unlocked ? (
          <button
            type="button"
            onClick={async () => {
              await lock();
              try {
                window.localStorage.removeItem(ELEKTRYK_ADMIN_TOKEN_KEY);
              } catch {}
              onLocked();
              toast.success("Wylogowano");
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70 hover:border-white/30 hover:text-white"
          >
            <LogOut size={12} /> Wyloguj
          </button>
        ) : (
          <span className="w-[110px]" />
        )}
      </div>
    </header>
  );
}

function UnlockForm({ onUnlocked }: { onUnlocked: () => void }) {
  const unlock = useServerFn(elektrykAdminUnlock);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await unlock({ data: { password } });
      if (r.ok && r.token) {
        try {
          window.localStorage.setItem(ELEKTRYK_ADMIN_TOKEN_KEY, r.token);
        } catch {}
        onUnlocked();
        toast.success("Zalogowano");
      } else toast.error("Nieprawidłowe hasło");
    } catch {
      toast.error("Błąd logowania");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md py-20">
      <div className="rounded-3xl border border-white/[0.07] bg-[#0A0A0C] p-8">
        <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">Wymagane hasło</div>
        <h1 className="mt-3 text-2xl font-light tracking-tight text-white">Dostęp do panelu</h1>
        <p className="mt-2 text-sm text-white/60">Wpisz wspólne hasło ustalone dla demo.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3.5 pr-12 text-sm text-white outline-none focus:border-[#00F0FF]/50"
              placeholder="Hasło"
              required
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              aria-label="Pokaż hasło"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition disabled:opacity-50"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 30px ${VOLT}55` }}
          >
            {loading ? "Sprawdzam…" : "Zaloguj"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard() {
  const listFn = useServerFn(elektrykAdminListRealizacje);
  const createFn = useServerFn(elektrykAdminCreateRealizacja);
  const [items, setItems] = useState<AdminRealizacja[] | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const r = (await listFn()) as AdminRealizacja[];
      setItems(r);
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd ładowania");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await createFn({ data: { title: newTitle.trim() } });
      setNewTitle("");
      toast.success("Dodano realizację");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-white">Realizacje</h1>
          <p className="mt-1 text-sm text-white/60">
            Dodawaj nowe, edytuj tytuł i opis, wgrywaj wiele zdjęć dla każdej.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">
          Wszystkich: {items?.length ?? "—"}
        </div>
      </div>

      <form
        onSubmit={create}
        className="mt-6 flex flex-col gap-3 rounded-2xl border border-white/[0.07] bg-[#0A0A0C] p-4 sm:flex-row sm:items-center"
      >
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Tytuł nowej realizacji…"
          className="flex-1 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none focus:border-[#00F0FF]/50"
        />
        <button
          type="submit"
          disabled={creating || !newTitle.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-black disabled:opacity-50"
          style={{ backgroundColor: VOLT }}
        >
          <Plus size={14} /> Dodaj
        </button>
      </form>

      <div className="mt-8 space-y-4">
        {items === null && (
          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.02] p-10 text-center text-sm text-white/50">
            Ładowanie…
          </div>
        )}
        {items && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 bg-transparent p-10 text-center text-sm text-white/55">
            Brak realizacji. Dodaj pierwszą powyżej.
          </div>
        )}
        {items?.map((it) => (
          <RealizationCard key={it.id} item={it} reload={load} />
        ))}
      </div>
    </div>
  );
}

function RealizationCard({ item, reload }: { item: AdminRealizacja; reload: () => Promise<void> }) {
  const updateFn = useServerFn(elektrykAdminUpdateRealizacja);
  const deleteFn = useServerFn(elektrykAdminDeleteRealizacja);
  const uploadFn = useServerFn(elektrykAdminUploadImage);
  const delImgFn = useServerFn(elektrykAdminDeleteImage);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? "");
  const [location, setLocation] = useState(item.location ?? "");
  const [dateLabel, setDateLabel] = useState(item.date_label ?? "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function save() {
    setBusy(true);
    try {
      await updateFn({
        data: {
          id: item.id,
          title,
          description: description || null,
          location: location || null,
          date_label: dateLabel || null,
        },
      });
      toast.success("Zapisano");
      setEditing(false);
      await reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd zapisu");
    } finally {
      setBusy(false);
    }
  }

  async function togglePublished() {
    setBusy(true);
    try {
      await updateFn({ data: { id: item.id, published: !item.published } });
      await reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Usunąć realizację "${item.title}" wraz ze zdjęciami?`)) return;
    setBusy(true);
    try {
      await deleteFn({ data: { id: item.id } });
      toast.success("Usunięto");
      await reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd");
    } finally {
      setBusy(false);
    }
  }

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("realizacjaId", item.id);
        await uploadFn({ data: fd });
      }
      toast.success(`Wgrano ${files.length} zdjęć`);
      await reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd uploadu");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removeImage(id: string) {
    if (!confirm("Usunąć to zdjęcie?")) return;
    try {
      await delImgFn({ data: { id } });
      await reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Błąd");
    }
  }

  return (
    <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A0C] p-5 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="space-y-3">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-base font-medium text-white outline-none focus:border-[#00F0FF]/50"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Lokalizacja"
                  className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-xs text-white outline-none focus:border-[#00F0FF]/50"
                />
                <input
                  value={dateLabel}
                  onChange={(e) => setDateLabel(e.target.value)}
                  placeholder="Data (np. 06.2026)"
                  className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-xs text-white outline-none focus:border-[#00F0FF]/50"
                />
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Opis realizacji…"
                className="w-full resize-none rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00F0FF]/50"
              />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-medium text-white">{item.title}</h3>
                {!item.published && (
                  <span className="rounded-full bg-orange-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-orange-300">
                    Ukryta
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/45">
                {item.location || "—"} · {item.date_label || "—"} · {item.images.length}{" "}
                {item.images.length === 1 ? "zdjęcie" : "zdjęć"}
              </div>
              {item.description && (
                <p className="mt-3 text-sm leading-relaxed text-white/65">{item.description}</p>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-black disabled:opacity-50"
                style={{ backgroundColor: VOLT }}
              >
                <Save size={12} /> Zapisz
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setTitle(item.title);
                  setDescription(item.description ?? "");
                  setLocation(item.location ?? "");
                  setDateLabel(item.date_label ?? "");
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-white/70 hover:text-white"
              >
                <X size={12} /> Anuluj
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-[11px] uppercase tracking-[0.18em] text-white/75 hover:border-[#00F0FF]/40 hover:text-white"
              >
                <Pencil size={12} /> Edytuj
              </button>
              <button
                onClick={togglePublished}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-[11px] uppercase tracking-[0.18em] text-white/70 hover:text-white disabled:opacity-50"
              >
                {item.published ? <EyeOff size={12} /> : <Eye size={12} />}
                {item.published ? "Ukryj" : "Pokaż"}
              </button>
              <button
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-3.5 py-2 text-[11px] uppercase tracking-[0.18em] text-red-300 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 size={12} /> Usuń
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-white/[0.06] pt-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/55">
            <ImageIcon size={12} /> Zdjęcia
          </div>
          <label
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/75 hover:border-[#00F0FF]/40 hover:text-white"
            style={uploading ? { opacity: 0.5, pointerEvents: "none" } : undefined}
          >
            <Upload size={12} />
            {uploading ? "Wgrywam…" : "Dodaj zdjęcia"}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => upload(e.target.files)}
            />
          </label>
        </div>
        {item.images.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-xs text-white/45">
            Brak zdjęć. Dodaj przynajmniej jedno, żeby realizacja pokazała się publicznie.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {item.images.map((img) => (
              <div
                key={img.id}
                className="group relative aspect-square overflow-hidden rounded-lg border border-white/[0.06] bg-black/40"
              >
                <img
                  src={img.url}
                  alt={img.alt ?? ""}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <button
                  onClick={() => removeImage(img.id)}
                  className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/70 text-white/80 opacity-0 transition group-hover:opacity-100"
                  aria-label="Usuń zdjęcie"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}